import React, { useState } from "react";
import { Box, Card, CardContent, Chip, Divider, Grid2, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { useNavigate, useNavigationType, useParams } from "react-router-dom";
import moment from "moment";
import HistoryTimeLine from "components/HistoryTimeLine";
import { dispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { usePermission } from "hooks/usePermission";
import { KEY_ISSUE_ACCESS } from "config";
import { KEY_ISSUE_EDIT } from "config";
import { useGetScheduleDPRDetailQuery } from "store/slices/apis/scheduleDprSlice";
import TableComponent from "components/grid";
import AddScheduleDPRComment from "./AddScheduleDPRComment";
import { ReactComponent as LeftArrowicon } from "assets/PwC_Funct_Icons_ArrowLeft_Outline_Black_RGB.svg"

const columns = [
    {
        name: 'Workflow name',
        field: 'workflowApproval',
        renderer(params) {
            return (
                <Chip size="small" sx={{
                    fontSize: '12px',
                    '& .MuiChip-label': {
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        fontWeight: 600
                    }
                }} label={`${params?.workflowName ?? 'N/A'}`} />
            )
        }
    },
    {
        name: 'Reporting Date',
        field: 'reportedDate',
        renderer(params) {
            return (
                <>
                    {params
                        ? moment(params).format("DD-MM-YYYY")
                        : "N.A."}
                </>
            );
        },
    },
    {
        name: 'Activity Count',
        field: 'activityCount'
    },
    {
        name: 'Created at',
        field: 'createddate',
        renderer(params) {
            return (
                <>
                    {params
                        ? moment(params).format("DD-MM-YYYY")
                        : "N.A."}
                </>
            );
        },
    },
]

const quantityDprDetails = [
    {
        name: 'Activity Name',
        field: 'activityName'
    },
    {
        name: 'Activity Code',
        field: 'activityCode'
    },
    {
        name: 'UOM',
        field: 'uom',
        renderer(params) {
            return (
                <>
                    {params?.uom
                        ? params?.uom
                        : "N.A."}
                </>
            );
        },
    },
    {
        name: 'BOQ',
        field: 'boq'
    },
    {
        name: 'Planned Qty Till Date',
        field: 'plannedQtyTillDate'
    },
    {
        name: 'Actual Qty Till Date',
        field: 'actualQtyTillDate'
    },
    {
        name: 'Overall Progress',
        field: 'overallProgress'
    },
    {
        name: 'ReportedDate',
        field: 'reportedDate',
        renderer(params) {
            return (
                <>
                    {params
                        ? moment(params).format("DD-MM-YYYY")
                        : "N.A."}
                </>
            );
        },
    },
    {
        name: 'Planned Quantity',
        field: 'plannedQuantity'
    },
    {
        name: 'Actual Quantity',
        field: 'actualQuantity',
    },
    {
        name: 'Planned Manpower',
        field: 'plannedManpower',
    },
    {
        name: 'Actual Manpower',
        field: 'actualManpower',
    },
    {
        name: 'Percentage',
        field: 'percentage',
    },
]


const ScheduleDPRDetail = () => {

    const theme = useTheme();
    const navigate = useNavigate();
    const navigationType = useNavigationType();

    const { id, assignee, isEditable } = useParams();

    const decrypt = (encoded) => decodeURIComponent(escape(atob(encoded)));
    const decrypted = decrypt(assignee);

    const detailResponse = useGetScheduleDPRDetailQuery(id);
    const canApprove = usePermission(KEY_ISSUE_ACCESS);
    const canEdit = usePermission(KEY_ISSUE_EDIT);

    const [isEditIssueModal, setEditIssueModal] = useState(false);
    const [workflowStatus, setWorkflowStatus] = useState('N/A');
    const [rowData, setRowData] = useState(false);

    const handleEditIssue = (row) => {
        setEditIssueModal((prev) => {
            const newState = !prev;
            setRowData(newState ? row : null);
            return newState;
        });
    }

    const openNotification = async (msg, type = 'error') => {
        await dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), 3000);
    };

    return (
        <>
            <Grid2 container spacing={2} sx={{ mt: '20px' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h5" sx={{ fontSize: '24px', fontWeight: '500', color: '#252525', mb: 2, lineHeight: "32px", letterSpacing: '-0.4px' }}>
                        <Tooltip
                            placement="bottom"
                            title="Back"
                            arrow
                            slotProps={{
                                tooltip: { sx: { fontSize: "12px" } },
                            }}
                        >
                            {window.history.length > 1 && navigationType !== "POP" &&
                                <IconButton
                                    aria-label="download"
                                    size="small"
                                    onClick={() => {
                                        navigate(-1)
                                    }}
                                >
                                    <LeftArrowicon style={{ height: '22px', width: '22px', color: "#252525", stroke: "#252525", strokeWidth: 4 }} />
                                </IconButton>
                            }
                        </Tooltip>

                        Schedule DPR Detail
                    </Typography>
                </Box>
            </Grid2>
            <Grid2 container spacing={2} sx={{ mt: '20px' }}>
                <Grid2 size={12}>
                    <Card sx={{ borderRadius: 1, boxShadow: 'none', p: 3, pt: 1.5 }}>
                        <Box sx={{ width: '100%', mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: '20px',
                                    fontWeight: '500',
                                    color: '#252525',
                                    lineHeight: '32px',
                                    letterSpacing: '-0.4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                {`Schedule DPR`}
                            </Typography>
                            {/* {canEdit && detailResponse?.data?.data?.currentStatus != 'Close' && isEditable == 'true' &&
                                <Tooltip
                                    placement="top"
                                    title={"Edit"}
                                    arrow
                                    slotProps={{
                                        tooltip: { sx: { fontSize: "12px" } },
                                    }}
                                >
                                    <IconButton
                                        aria-label="Edit Issue"
                                        size="small"
                                        onClick={() => handleEditIssue(detailResponse?.data?.data)}
                                    >
                                        <FaRegEdit />
                                    </IconButton>
                                </Tooltip>
                            } */}

                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <CardContent sx={{ p: 0 }}>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, md: 2.4, sm: 4 }}>
                                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>Workflow status</Typography>
                                    {/* <Typography gutterBottom variant="body2">At RMC</Typography> */}
                                    <Chip size="small" sx={{
                                        fontSize: '12px',
                                        '& .MuiChip-label': {
                                            paddingLeft: '8px',
                                            paddingRight: '8px',
                                            fontWeight: 600
                                        }
                                    }} label={`${workflowStatus}`} />
                                </Grid2>
                                {columns?.map((val, index) => {
                                    return (
                                        <Grid2 key={index} size={{ xs: 12, md: 2.4, sm: 4 }}>
                                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>{val?.name}</Typography>
                                            {val?.renderer ?
                                                val.renderer(detailResponse?.data?.data[val?.field])
                                                :
                                                <Typography gutterBottom variant="body2">{detailResponse?.data?.data[val?.field]}</Typography>
                                            }
                                        </Grid2>
                                    )
                                })}

                            </Grid2>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={12}>
                    <TableComponent
                        columns={quantityDprDetails}
                        data={detailResponse?.data?.data?.quantityDprDetails}
                    // isLoading={isLoading}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <HistoryTimeLine id={id} workflow={setWorkflowStatus} />
                </Grid2>
                {canApprove && decrypted == 'edit' && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Card sx={{ borderRadius: 1, boxShadow: 'none', p: 3, pt: 1.5, minHeight: '250px' }}>
                            <Box sx={{ width: '100%', mb: 1.5 }}>
                                <Typography variant="h5" sx={{ fontSize: '20px', fontWeight: '500', color: '#252525', lineHeight: "32px", letterSpacing: '-0.4px' }}>
                                    Add comment
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <CardContent sx={{ p: 0 }}>
                                <AddScheduleDPRComment id={id} rowData={detailResponse?.data?.data} />
                            </CardContent>
                        </Card>
                    </Grid2>
                )}

            </Grid2 >

            {/* {isEditIssueModal &&
                <EditIssue cancelIssue={handleEditIssue} open={isEditIssueModal} data={rowData} openNotification={openNotification} />
            } */}

        </>
    );
}

export default ScheduleDPRDetail;
